import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Upload, Tag, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined, RobotOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { PAYMENT_STATUS_OPTIONS, VAT_PERCENT, formatOmr } from "./constants";
import {
  listCompanyExpenses, getCompanyExpenseKpis, createCompanyExpense,
  updateCompanyExpense, deleteCompanyExpense, exportCompanyExpenses,
} from "../../services/accounts.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function CompanyExpensesTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  const { options: billerOptions } = useSettingsDropdown("incentive-billers");
  const { options: categoryOptions } = useSettingsDropdown("biller-categories");
  const { options: expenseTypeOptions } = useSettingsDropdown("expense-emi-types", {
    filter: (item) => item.expenseCategory !== "rider",
  });
  const { options: bankOptions } = useSettingsDropdown("company-bank-accounts");
  const { options: paymentModeOptions } = useSettingsDropdown("payment-modes");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState();
  const [category, setCategory] = useState();
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      paymentStatus: paymentStatus || undefined,
      category: category || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, paymentStatus, category, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listCompanyExpenses(params({ page, per_page: pageSize })),
        getCompanyExpenseKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load company expenses");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, paymentStatus, category, dateRange]);

  const openModal = (record = null) => {
    setAttachment(null);
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        invoiceDate: record.invoiceDate ? dayjs(record.invoiceDate) : null,
        invoiceNo: record.invoiceNo,
        biller: record.biller?._id,
        category: record.category?._id,
        expenseType: record.expenseType?._id,
        expenseName: record.expenseName,
        expenseAmount: record.expenseAmount,
        vat: record.vat,
        paymentStatus: record.paymentStatus,
        sourceBankAccount: record.sourceBankAccount?._id,
        paymentMode: record.paymentMode?._id,
        invoicePaidDate: record.invoicePaidDate ? dayjs(record.invoicePaidDate) : null,
        transactionId: record.transactionId,
        description: record.description,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ invoiceDate: dayjs(), paymentStatus: "notPaid", vat: 0 });
    }
  };

  /** Convenience: derive VAT from the gross amount at the standard rate. */
  const applyStandardVat = () => {
    const gross = Number(form.getFieldValue("expenseAmount") || 0);
    if (!gross) return;
    // Gross is VAT-inclusive, so the tax is gross × r / (100 + r).
    const vat = Number(((gross * VAT_PERCENT) / (100 + VAT_PERCENT)).toFixed(3));
    form.setFieldsValue({ vat });
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        invoiceDate: values.invoiceDate.format("YYYY-MM-DD"),
        invoicePaidDate: values.invoicePaidDate?.format("YYYY-MM-DD"),
        ...(attachment ? { attachment } : {}),
      };
      if (modal.record) {
        await updateCompanyExpense(modal.record._id, payload);
        message.success("Expense updated");
      } else {
        await createCompanyExpense(payload);
        message.success("Expense added");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save expense");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Company Expense",
      content: `Delete ${record.invoiceId}?`,
      onConfirm: async () => {
        try {
          await deleteCompanyExpense(record._id);
          message.success("Expense deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete expense");
        }
      },
    });
  };

  const columns = [
    {
      title: "Invoice ID",
      dataIndex: "invoiceId",
      render: (value, r) => (
        <Space size={4}>
          {value}
          {r.isAutoGenerated && (
            <Tag icon={<RobotOutlined />} color="blue">
              auto
            </Tag>
          )}
        </Space>
      ),
    },
    { title: "Invoice Date", dataIndex: "invoiceDateLabel" },
    { title: "Invoice No", dataIndex: "invoiceNo" },
    { title: "Biller", dataIndex: "billerName" },
    { title: "Category", dataIndex: "categoryName" },
    { title: "Expense Name", dataIndex: "expenseName", ellipsis: true },
    { title: "Amount (OMR)", dataIndex: "expenseAmount", align: "right", render: formatOmr },
    { title: "VAT", dataIndex: "vat", align: "right", render: formatOmr },
    { title: "Net", dataIndex: "netAmount", align: "right", render: formatOmr },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      render: (value, r) => (
        <Tag color={value === "paid" ? "green" : "gold"}>{r.paymentStatusLabel}</Tag>
      ),
    },
    { title: "Bank Account", dataIndex: "sourceBankAccountName" },
  ];

  return (
    <div>
      <KPIGrid minWidth={160}>
        <KPICard label="All Expenses" value={kpis.all} />
        <KPICard label="Paid" value={kpis.paid} tone="positive" />
        <KPICard label="Not Paid" value={kpis.notPaid} />
        <KPICard label="Total Amount" value={kpis.totalAmount} unit="OMR" />
        <KPICard label="Paid Amount" value={kpis.paidAmount} unit="OMR" tone="positive" />
        <KPICard label="Outstanding" value={kpis.outstandingAmount} unit="OMR" tone="negative" />
        <KPICard label="Total VAT" value={kpis.totalVat} unit="OMR" />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search invoice, expense or transaction"
          allowClear
          style={{ width: 280 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Payment Status"
          allowClear
          style={{ width: 170 }}
          options={PAYMENT_STATUS_OPTIONS}
          value={paymentStatus}
          onChange={setPaymentStatus}
        />
        <Select
          placeholder="Category"
          allowClear
          style={{ width: 180 }}
          options={categoryOptions}
          value={category}
          onChange={setCategory}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Company_Expenses"
          onExport={() => exportCompanyExpenses(params())}
        />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Add Expense
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Company Expense" : "Add Company Expense"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={680}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="invoiceDate" label="Invoice Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="invoiceNo" label="Invoice No">
            <Input placeholder="The supplier's own invoice number" />
          </Form.Item>
          <Form.Item name="biller" label="Biller">
            <Select options={billerOptions} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Select options={categoryOptions} allowClear />
          </Form.Item>
          <Form.Item name="expenseType" label="Expense Type">
            <Select options={expenseTypeOptions} allowClear />
          </Form.Item>
          <Form.Item name="expenseName" label="Expense Name">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="expenseAmount"
            label="Expense Amount (OMR, incl. VAT)"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: "100%" }} precision={3} />
          </Form.Item>
          <Form.Item
            name="vat"
            label="VAT (OMR)"
            extra={
              <Button type="link" size="small" style={{ padding: 0 }} onClick={applyStandardVat}>
                Calculate at {VAT_PERCENT}% of the gross amount
              </Button>
            }
          >
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item name="paymentStatus" label="Payment Status">
            <Select options={PAYMENT_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="sourceBankAccount"
            label="Source Bank Account"
            extra="Marking an expense Paid posts it to the ledger as an outward movement."
          >
            <Select options={bankOptions} allowClear />
          </Form.Item>
          <Form.Item name="paymentMode" label="Payment Mode">
            <Select options={paymentModeOptions} allowClear />
          </Form.Item>
          <Form.Item name="invoicePaidDate" label="Invoice Paid Date">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="transactionId" label="Transaction ID">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
          <Form.Item label="Attachment">
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setAttachment(file);
                return false;
              }}
              onRemove={() => setAttachment(null)}
              fileList={attachment ? [attachment] : []}
            >
              <Button icon={<UploadOutlined />}>Select file (max 2 MB)</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
